import React, { useState, useEffect, useRef } from 'react';
import { activateOnKey } from '../utils/a11y';
import styles from '../styles/Todo.module.css';

/**
 * Todo - Unified todo component with multiple presentation variants
 *
 * Variants:
 * - "chat" - Inline card in chat messages (previously TodoWidget)
 * - "progress" - Compact display in progress bar
 * - "list" - Full display in todo list with edit/delete
 *
 * Props:
 * - todoId: number (required)
 * - todoText: string (required)
 * - isCompleted: boolean (required)
 * - isOptional: boolean (required)
 * - variant: "chat" | "progress" | "list" (default: "list")
 * - onToggle: function (optional) - callback after successful toggle
 * - onEdit: function (optional) - callback when edit button clicked (list variant only)
 * - onDelete: function (optional) - callback when delete button clicked (list variant only)
 * - showActions: boolean (optional) - show edit/delete actions in list variant (default: false)
 * - isEditing: boolean (optional) - whether this todo is currently being edited
 * - editValue: string (optional) - current edit value
 * - onEditChange: function (optional) - callback when edit value changes
 * - onEditSave: function (optional) - callback when save button clicked
 * - onEditCancel: function (optional) - callback when cancel button clicked
 */
function Todo({
  todoId,
  todoText,
  isCompleted,
  isOptional,
  variant = 'list',
  onToggle,
  onEdit,
  onDelete,
  showActions = false,
  isEditing = false,
  editValue = '',
  onEditChange,
  onEditSave,
  onEditCancel
}) {
  const [completed, setCompleted] = useState(isCompleted);
  const [isToggling, setIsToggling] = useState(false);
  const togglingRef = useRef(false);
  const [error, setError] = useState(null);

  // Sync local state when prop changes ONLY if not actively toggling
  // This prevents prop updates from overwriting user's visual state during API call
  useEffect(() => {
    if (!isToggling) {
      setCompleted(isCompleted);
    }
  }, [isCompleted, isToggling]);

  const handleToggle = async (e) => {
    if (e) e.stopPropagation();

    // Don't toggle if no callback provided (locked todos) or if already toggling.
    // The lock is a REF, not the isToggling state: state only updates on the
    // next render, so rapid taps on a slow device all read `false` and each
    // fired its own toggle — two toggles cancel out and the task looked stuck.
    if (!onToggle || togglingRef.current) {
      return;
    }
    togglingRef.current = true;

    // Set loading state - NO optimistic update
    setIsToggling(true);

    const release = () => {
      togglingRef.current = false;
      setIsToggling(false);
    };

    // Call parent callback (parent handles the API call and cache refresh)
    onToggle({
      todoId,
      onComplete: release,
      onError: (errorMsg) => {
        // Error - show error and clear loading
        console.error('[Todo] Toggle ERROR:', errorMsg);
        setError(errorMsg || 'Failed to update. Please try again.');
        setTimeout(() => setError(null), 4000);
        release();
      }
    });
  };

  // Shown next to the task while the server round-trip is in flight, so a slow
  // connection reads as "working on it" instead of "my tap did nothing".
  const savingLabel = isToggling && (
    <span className={styles.savingLabel} role="status">Saving…</span>
  );

  // The row IS the completion toggle, so it is announced as a checkbox rather
  // than a button — aria-checked carries the state a plain button cannot.
  // Locked todos (no onToggle) do nothing, so they stay out of the tab order.
  const toggleLabel = `${isOptional ? 'Optional task' : 'Task'}: ${todoText}`;
  const toggleProps = onToggle
    ? {
        role: 'checkbox',
        tabIndex: 0,
        'aria-checked': completed,
        'aria-label': toggleLabel,
        'aria-busy': isToggling,
        'aria-disabled': isToggling,
        onClick: handleToggle,
        onKeyDown: activateOnKey(handleToggle)
      }
    : {};

  // Render based on variant
  if (variant === 'chat') {
    return (
      <>
        <div
          className={`${styles.todoChatVariant} ${isOptional ? styles.optional : ''} ${
            completed ? styles.completed : ''
          } ${isToggling ? styles.loading : ''}`}
          {...toggleProps}
        >
          <div className={styles.checkbox} aria-hidden="true">
            {isToggling ? (
              <div className={styles.spinner}></div>
            ) : (
              completed ? '☑' : '☐'
            )}
          </div>
          <div className={styles.text}>
            {isOptional && <span className={styles.optionalBadge}>Optional: </span>}
            {todoText}
          </div>
          {savingLabel}
        </div>
        {error && <div className={styles.error}>{error}</div>}
      </>
    );
  }

  if (variant === 'progress') {
    return (
      <div className={styles.todoProgressVariant}>
        <span className={styles.checkbox}>☐</span>
        <span className={styles.progressText}>{todoText}</span>
      </div>
    );
  }

  // Default: list variant
  return (
    <>
      <div
        className={`${styles.todoListVariant} ${
          isOptional ? styles.optional : ''
        } ${completed ? styles.completed : ''} ${isToggling ? styles.loading : ''}`}
      >
        {/* While editing this row holds a text input, so it must not claim
            checkbox semantics — the toggle is suspended until editing ends,
            which is what the old `!isEditing` click guard already did. */}
        <div
          className={styles.todoContent}
          {...(isEditing ? {} : toggleProps)}
        >
          <div className={styles.checkbox} aria-hidden="true">
            {isToggling ? (
              <div className={styles.spinner}></div>
            ) : (
              completed ? '☑' : '☐'
            )}
          </div>
          {isEditing ? (
            <input
              type="text"
              className={styles.inlineInput}
              value={editValue}
              onChange={(e) => onEditChange && onEditChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && onEditSave) onEditSave();
                if (e.key === 'Escape' && onEditCancel) onEditCancel();
              }}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          ) : (
            <div className={styles.todoText}>
              {isOptional && <span className={styles.optionalBadge}>*</span>}
              {todoText}
            </div>
          )}
          {savingLabel}
        </div>
        {isEditing ? (
          <div className={styles.editActions}>
            <button
              className={styles.saveButton}
              onClick={(e) => {
                e.stopPropagation();
                onEditSave && onEditSave();
              }}
              title="Save"
              aria-label={`Save changes to task: ${todoText}`}
            >
              <span aria-hidden="true">✓</span>
            </button>
            <button
              className={styles.deleteButton}
              onClick={(e) => {
                e.stopPropagation();
                onDelete && onDelete();
              }}
              title="Delete task"
              aria-label={`Delete task: ${todoText}`}
            >
              <span aria-hidden="true">×</span>
            </button>
            <button
              className={styles.cancelButton}
              onClick={(e) => {
                e.stopPropagation();
                onEditCancel && onEditCancel();
              }}
              title="Cancel"
              aria-label="Cancel editing"
            >
              <span aria-hidden="true">↩</span>
            </button>
          </div>
        ) : showActions && (
          <button
            className={styles.editButton}
            onClick={(e) => {
              e.stopPropagation();
              onEdit && onEdit();
            }}
            title="Edit"
            aria-label={`Edit task: ${todoText}`}
          >
            <span aria-hidden="true">···</span>
          </button>
        )}
      </div>
      {error && <div className={styles.error}>{error}</div>}
    </>
  );
}

export default Todo;
